import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Popover, Box } from '@mui/material';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  InputUI,
  AddButtonUi,
  DatagridPremiumUi,
  ColorCircleUi,
  ColorPaletteUI,
  CheckboxUI,
} from '../Interface';

const EditToolbar = (props) => {
  const { disabled, push, defaultField } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    defaultField.id = uuidv4();

    await push(defaultField);

    const fieldsToValidate = [
      `folderTemplateItem.${values.folderTemplateItem.length ?? 0}.name`,
      `folderTemplateItem.${values.folderTemplateItem.length ?? 0}.color`,
      `folderTemplateItem.${values.folderTemplateItem.length ?? 0}.protected`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const FolderTemplateItem = ({ disabled, defaultField }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [anchorEl, setAnchorEl] = useState(null);

  const getValueIndex = (values, id) => {
    return values.folderTemplateItem.findIndex((rc) => rc.id === id);
  };

  const handlePopoverOpen = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Mappa neve',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`folderTemplateItem.${valueIndex}.name`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.folderTemplateItem?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`folderTemplateItem.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`folderTemplateItem.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'color',
        headerName: 'Szín',
        type: 'string',
        editable: true,
        renderCell: (params) => {
          return <ColorCircleUi color={params.value} size={20} />;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <div className="ml-1 w-[100%] flex justify-center">
              <div
                className={`${!disabled ? 'cursor-pointer' : 'cursor-not-allowed'} rounded-full shadow-[rgba(0,_0,_0,_0.54)_0px_1px_8px] w-[20px] h-[20px] mt-2.5`}
                style={{ background: values.folderTemplateItem[valueIndex].color }}
                onClick={handlePopoverOpen}
              />

              <ColorPaletteUI
                anchorEl={anchorEl}
                handlePopoverClose={handlePopoverClose}
                onClick={(item) => {
                  setFieldValue(`folderTemplateItem.${valueIndex}.color`, item);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: item,
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        field: 'protected',
        type: 'boolean',
        headerName: 'Védett mappa',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`folderTemplateItem.${valueIndex}.protected`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.folderTemplateItem?.[valueIndex]?.checked} />}
              checked={values.folderTemplateItem?.[valueIndex]?.protected ?? false}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`folderTemplateItem.${valueIndex}.protected`, e.target.checked);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`folderTemplateItem.${valueIndex}.protected`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg mb-5 px-3">
          <FieldArray name="folderTemplateItem">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.FolderTemplateItem}
                height={600}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.folderTemplateItem ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultField,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default FolderTemplateItem;
